import { supabase } from "../lib/supabase";

export class ContactService {
  // Helper function to format phone numbers for display
  static formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;
    const cleaned = phoneNumber.replace(/\D/g, "");
    if (cleaned.length !== 10) return phoneNumber;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  }
  static async getAllContacts() {
    try {
      const { data, error } = await supabase
        .from("contacts")
        .select(
          `
          id,
          name,
          email,
          phone_number,
          role,
          client_id,
          billing_yn,
          notes,
          clients (
            id,
            name
          )
        `,
        )
        .order("name");

      if (error) {
        throw new Error(`Failed to fetch contacts: ${error.message}`);
      }

      const processedData = data.map((contact) => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phoneNumber: this.formatPhoneNumber(contact.phone_number),
        role: contact.role,
        clientId: contact.client_id,
        clientName: contact.clients?.name || "No Client",
        billing: contact.billing_yn || false,
        notes: contact.notes || "",
      }));

      return processedData;
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }

  static async getPaginatedContacts(
    page = 1,
    pageSize = 50,
    sortField = "name",
    sortDirection = "asc",
    searchQuery = "",
    clientFilter = null,
  ) {
    try {
      const offset = (page - 1) * pageSize;

      // Build the query with optional search
      let countQuery = supabase
        .from("contacts")
        .select("*", { count: "exact", head: true });
      let dataQuery = supabase.from("contacts").select(
        `
          id,
          name,
          email,
          phone_number,
          role,
          client_id,
          billing_yn,
          notes,
          clients (
            id,
            name
          )
        `,
      );

      // Apply client filter if provided
      if (clientFilter) {
        countQuery = countQuery.eq("client_id", clientFilter);
        dataQuery = dataQuery.eq("client_id", clientFilter);
      }

      // Apply search filters if search query is provided
      if (searchQuery && searchQuery.trim()) {
        const query = searchQuery.trim();

        // First, find client IDs that match the search term
        const { data: matchingClients } = await supabase
          .from("clients")
          .select("id")
          .ilike("name", `%${query}%`);

        const matchingClientIds = matchingClients?.map((c) => c.id) || [];

        // Build search filter that includes contact fields AND matching client IDs
        // Note: role is an enum type and cannot be searched with ilike
        if (matchingClientIds.length > 0) {
          // Apply search to count query (contact fields + matching client IDs)
          countQuery = countQuery.or(
            `name.ilike.%${query}%,email.ilike.%${query}%,phone_number.ilike.%${query}%,client_id.in.(${matchingClientIds.join(",")})`,
          );

          // Apply search to data query
          dataQuery = dataQuery.or(
            `name.ilike.%${query}%,email.ilike.%${query}%,phone_number.ilike.%${query}%,client_id.in.(${matchingClientIds.join(",")})`,
          );
        } else {
          // No matching clients, just search contact fields
          countQuery = countQuery.or(
            `name.ilike.%${query}%,email.ilike.%${query}%,phone_number.ilike.%${query}%`,
          );

          dataQuery = dataQuery.or(
            `name.ilike.%${query}%,email.ilike.%${query}%,phone_number.ilike.%${query}%`,
          );
        }
      }

      // Get total count for pagination info
      const { count, error: countError } = await countQuery;

      if (countError) {
        console.error("Count query error:", countError);
        throw new Error(
          `Failed to get contacts count: ${countError.message || countError.toString() || "Unknown error"}`,
        );
      }

      // Handle sorting - map UI field names to database field names
      let actualSortField = sortField;
      const fieldMapping = {
        clientName: "clients(name)",
        phoneNumber: "phone_number",
        billing: "billing_yn",
      };

      // Apply field mapping if it exists
      if (fieldMapping[sortField]) {
        actualSortField = fieldMapping[sortField];
      }

      // Get paginated data with sorting and range
      const { data, error } = await dataQuery
        .order(actualSortField, { ascending: sortDirection === "asc" })
        .range(offset, offset + pageSize - 1);

      if (error) {
        console.error("Data query error:", error);
        throw new Error(
          `Failed to fetch contacts: ${error.message || error.toString() || "Unknown error"}`,
        );
      }

      const processedData = data.map((contact) => ({
        id: contact.id,
        name: contact.name,
        email: contact.email,
        phoneNumber: this.formatPhoneNumber(contact.phone_number),
        role: contact.role,
        clientId: contact.client_id,
        clientName: contact.clients?.name || "No Client",
        billing: contact.billing_yn || false,
        notes: contact.notes || "",
      }));

      return {
        data: processedData,
        totalCount: count,
        totalPages: Math.ceil(count / pageSize),
        currentPage: page,
        pageSize: pageSize,
        hasNextPage: page < Math.ceil(count / pageSize),
        hasPreviousPage: page > 1,
      };
    } catch (error) {
      console.error("Error fetching paginated contacts:", error);
      throw error;
    }
  }

  static async updateContact(contactId, fieldOrData, value) {
    try {
      let updateData;

      // Handle both single field update and full object update
      if (typeof fieldOrData === "string") {
        // Single field update
        const dbField = this.mapFieldToDatabase(fieldOrData);
        updateData = { [dbField]: value };
      } else {
        // Full object update
        // Clean phone number - remove formatting before saving
        let cleanedPhoneNumber = fieldOrData.phoneNumber || null;
        if (cleanedPhoneNumber) {
          cleanedPhoneNumber = cleanedPhoneNumber.replace(/\D/g, "");
        }

        updateData = {
          name: fieldOrData.name,
          email: fieldOrData.email || null,
          phone_number: cleanedPhoneNumber,
          role: fieldOrData.role,
          client_id:
            fieldOrData.clientId === "" || fieldOrData.clientId == null
              ? null
              : Number(fieldOrData.clientId),
          billing_yn: fieldOrData.billing || false,
          notes: fieldOrData.notes || null,
        };
      }

      // Perform the update without select to work around RLS restrictions
      const { error: updateError } = await supabase
        .from("contacts")
        .update(updateData)
        .eq("id", contactId);

      if (updateError) {
        console.error("Update error:", updateError);
        throw new Error(`Failed to update contact: ${updateError.message}`);
      }
      // Wait a moment to ensure database has processed the update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Fetch the updated contact separately (this works with RLS)
      const { data: updatedContact, error: selectError } = await supabase
        .from("contacts")
        .select(
          `
          id,
          name,
          email,
          phone_number,
          role,
          client_id,
          billing_yn,
          notes,
          clients (
            id,
            name
          )
        `,
        )
        .eq("id", contactId)
        .single();
      if (selectError) {
        throw new Error(
          `Failed to fetch updated contact: ${selectError.message}`,
        );
      }

      if (!updatedContact) {
        throw new Error("Contact was updated but could not be retrieved");
      }
      return {
        id: updatedContact.id,
        name: updatedContact.name,
        email: updatedContact.email,
        phoneNumber: this.formatPhoneNumber(updatedContact.phone_number),
        role: updatedContact.role,
        clientId: updatedContact.client_id,
        clientName: updatedContact.clients?.name || "No Client",
        billing: updatedContact.billing_yn || false,
        notes: updatedContact.notes || "",
      };
    } catch (error) {
      console.error("Error updating contact:", error);
      throw error;
    }
  }

  static async deleteContact(contactId) {
    try {
      const { error } = await supabase
        .from("contacts")
        .delete()
        .eq("id", contactId);

      if (error) {
        throw new Error(`Failed to delete contact: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error("Error deleting contact:", error);
      throw error;
    }
  }

  static mapFieldToDatabase(field) {
    const fieldMap = {
      name: "name",
      email: "email",
      phoneNumber: "phone_number",
      role: "role",
      clientId: "client_id",
      billing: "billing_yn",
      notes: "notes",
    };
    return fieldMap[field] || field;
  }

  static async createContact(contactData) {
    try {
      // Clean phone number - remove formatting before saving
      let cleanedPhoneNumber = contactData.phoneNumber || null;
      if (cleanedPhoneNumber) {
        cleanedPhoneNumber = cleanedPhoneNumber.replace(/\D/g, "");
      }

      const { data, error } = await supabase
        .from("contacts")
        .insert([
          {
            name: contactData.name,
            email: contactData.email || null,
            phone_number: cleanedPhoneNumber,
            role: contactData.role,
            client_id:
              contactData.clientId === "" || contactData.clientId == null
                ? null
                : Number(contactData.clientId),
            billing_yn: contactData.billing || false,
            notes: contactData.notes || null,
          },
        ])
        .select(
          `
          id,
          name,
          email,
          phone_number,
          role,
          client_id,
          billing_yn,
          notes,
          clients (
            id,
            name
          )
        `,
        );
      if (error) {
        throw new Error(`Failed to create contact: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error("Failed to create contact - no data returned");
      }

      const newContact = data[0];
      return {
        id: newContact.id,
        name: newContact.name,
        email: newContact.email,
        phoneNumber: this.formatPhoneNumber(newContact.phone_number),
        role: newContact.role,
        clientId: newContact.client_id,
        clientName: newContact.clients?.name || "No Client",
        billing: newContact.billing_yn || false,
        notes: newContact.notes || "",
      };
    } catch (error) {
      console.error("Error creating contact:", error);
      throw error;
    }
  }
}

// Debug function for testing contact updates
if (typeof window !== "undefined") {
  window.testContactUpdate = async (contactId = 5471) => {
    try {
      // Get current auth session
      await supabase.auth.getSession();
      // Fetch the contact
      const { error: fetchError } = await supabase
        .from("contacts")
        .select("*")
        .eq("id", contactId)
        .single();

      if (fetchError) {
        console.error("Fetch error:", fetchError);
        return;
      }
      // Try to update with a test name
      const testName = `Test Update ${new Date().toLocaleTimeString()}`;
      // Try update with explicit return
      await supabase
        .from("contacts")
        .update({ name: testName })
        .eq("id", contactId)
        .select()
        .single();
      // Wait a moment before verifying
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Verify the update
      const { data: verifyData } = await supabase
        .from("contacts")
        .select("*")
        .eq("id", contactId)
        .single();
      if (verifyData && verifyData.name === testName) {
      } else {
      }

      return verifyData;
    } catch (error) {
      console.error("Test failed:", error);
    }
  };

  console.log(
    "Contact update test available: window.testContactUpdate(contactId)",
  );

  // Test RLS policies
  window.testContactRLS = async () => {
    try {
      // Check auth
      await supabase.auth.getUser();
      // Test INSERT
      const testContact = {
        name: "RLS Test Contact",
        email: "rls@test.com",
        role: "Estimator", // Use a valid enum value
        client_id: 1513,
      };

      const { data: insertData, error: insertError } = await supabase
        .from("contacts")
        .insert(testContact)
        .select()
        .single();

      if (insertError) {
        console.error("INSERT failed:", insertError.message);
      } else {
        // Try to update the inserted contact
        const { error: updateError } = await supabase
          .from("contacts")
          .update({ name: "RLS Test Updated" })
          .eq("id", insertData.id);

        if (updateError) {
          console.error("UPDATE failed:", updateError.message);
        } else {
        }

        // Clean up
        const { error: deleteError } = await supabase
          .from("contacts")
          .delete()
          .eq("id", insertData.id);

        if (deleteError) {
          console.error("DELETE failed:", deleteError.message);
        } else {
        }
      }
    } catch (error) {
      console.error("RLS test failed:", error);
    }
  };

  console.log("RLS test available: window.testContactRLS()");
}
